import type {
    ContentDefinition,
    DocumentBlock,
    ForDefinition,
    PluginDefinition,
    PluginOutput,
} from "@magic-md/core";
import { parseMagicMd } from "@magic-md/core";

export interface ScopeLookupNode {
    definitions: Map<string, PluginDefinition>;
    forDefinitions: Map<string, ForDefinition>;
    contentDefinitions: Map<string, ContentDefinition>;
    unitOutputs: Record<string, PluginOutput>;
    parent: ScopeLookupNode | null;
}

export function contentNeedsExecution(content: ContentDefinition): boolean {
    const hasExternalRef = /\$ref\{(?!input\/)[^}]+\}/.test(content.body);
    if (hasExternalRef) return true;

    try {
        const parsed = parseMagicMd(content.body);
        if (parsed.definitions.size > 0) return true;
        if ((parsed.forDefinitions?.size ?? 0) > 0) return true;
        if ((parsed.imports?.length ?? 0) > 0) return true;
        if ((parsed.uses?.length ?? 0) > 0) return true;
        for (const nested of parsed.contentDefinitions?.values() ?? []) {
            if (contentNeedsExecution(nested)) return true;
        }
    } catch {
        return content.body.includes("::define[")
            || content.body.includes("::for[")
            || content.body.includes("::import{")
            || content.body.includes("::use[");
    }

    return false;
}

export function findDefinitionInScope(
    scope: ScopeLookupNode | null,
    id: string,
): PluginDefinition | undefined {
    let current = scope;
    while (current) {
        const def = current.definitions.get(id);
        if (def) return def;
        current = current.parent;
    }
    return undefined;
}

export function findForDefinitionInScope(
    scope: ScopeLookupNode | null,
    id: string,
): ForDefinition | undefined {
    let current = scope;
    while (current) {
        const def = current.forDefinitions.get(id);
        if (def) return def;
        current = current.parent;
    }
    return undefined;
}

export function findContentDefinitionInScope(
    scope: ScopeLookupNode | null,
    id: string,
): ContentDefinition | undefined {
    let current = scope;
    while (current) {
        const def = current.contentDefinitions.get(id);
        if (def) return def;
        current = current.parent;
    }
    return undefined;
}

export function findOutputInScope(
    scope: ScopeLookupNode | null,
    id: string,
): PluginOutput | undefined {
    let current = scope;
    while (current) {
        const output = current.unitOutputs[id];
        if (output !== undefined) return output;
        current = current.parent;
    }
    return undefined;
}

export function groupBlocks(
    blocks: DocumentBlock[],
): (DocumentBlock | DocumentBlock[])[] {
    const result: (DocumentBlock | DocumentBlock[])[] = [];
    let inlineGroup: DocumentBlock[] = [];

    const flushGroup = () => {
        if (inlineGroup.length > 0) {
            const hasInline = inlineGroup.some(
                (b) => b.type === "inline-render" || b.type === "inline-ref",
            );
            if (hasInline) {
                result.push(inlineGroup);
            } else {
                for (const b of inlineGroup) result.push(b);
            }
            inlineGroup = [];
        }
    };

    for (const block of blocks) {
        if (block.type === "inline-render" || block.type === "inline-ref") {
            if (inlineGroup.length === 0) {
                const prev = result[result.length - 1];
                if (prev && !Array.isArray(prev) && prev.type === "markdown") {
                    result.pop();
                    const split = findInlineGroupStartBoundary(prev.content);
                    if (split) {
                        const before = prev.content.slice(0, split.index);
                        const after = prev.content.slice(split.afterIndex);
                        if (before.trim()) result.push({ ...prev, content: before });
                        if (after.trim()) inlineGroup.push({ ...prev, content: after });
                    } else {
                        inlineGroup.push(prev);
                    }
                }
            }
            inlineGroup.push(block);
        } else if (block.type === "markdown" && inlineGroup.length > 0) {
            const split = findInlineGroupBoundary(block.content);
            if (split) {
                const before = block.content.slice(0, split.index);
                const after = block.content.slice(split.afterIndex);
                if (before.trim()) inlineGroup.push({ ...block, content: before });
                flushGroup();
                if (after.trim()) {
                    if (split.continuesInlineContext) {
                        inlineGroup.push({ ...block, content: after });
                    } else {
                        result.push({ ...block, content: after });
                    }
                }
            } else {
                inlineGroup.push(block);
            }
        } else {
            flushGroup();
            result.push(block);
        }
    }

    flushGroup();
    return result;
}

function findInlineGroupStartBoundary(content: string): {
    index: number;
    afterIndex: number;
} | null {
    const blockBoundary = findLastMarkdownBlockLine(content);
    if (blockBoundary >= 0) {
        return {
            index: blockBoundary,
            afterIndex: blockBoundary + 1,
        };
    }

    const paraBreak = content.lastIndexOf("\n\n");
    if (paraBreak >= 0) {
        return {
            index: paraBreak + 2,
            afterIndex: paraBreak + 2,
        };
    }

    return null;
}

function findInlineGroupBoundary(content: string): {
    index: number;
    afterIndex: number;
    continuesInlineContext: boolean;
} | null {
    const paraBreak = content.indexOf("\n\n");
    const blockBoundary = findNextMarkdownBlockLine(content);

    if (paraBreak < 0 && blockBoundary < 0) return null;

    if (paraBreak >= 0 && (blockBoundary < 0 || paraBreak <= blockBoundary)) {
        return {
            index: paraBreak,
            afterIndex: paraBreak,
            continuesInlineContext: false,
        };
    }

    return {
        index: blockBoundary,
        afterIndex: blockBoundary + 1,
        continuesInlineContext: true,
    };
}

const MARKDOWN_BLOCK_LINE_BOUNDARY_RE =
    /\n(?= {0,3}(?:#{1,6}[ \t]+|[-+*][ \t]+|\d+[.)][ \t]+|>[ \t]?))/g;

function findNextMarkdownBlockLine(content: string): number {
    const pattern = new RegExp(MARKDOWN_BLOCK_LINE_BOUNDARY_RE);
    const match = pattern.exec(content);
    return match?.index ?? -1;
}

function findLastMarkdownBlockLine(content: string): number {
    const pattern = new RegExp(MARKDOWN_BLOCK_LINE_BOUNDARY_RE);
    let last = -1;
    let match: RegExpExecArray | null;
    while ((match = pattern.exec(content)) !== null) {
        last = match.index;
    }
    return last;
}
